import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { money, levelPayment, todayET, addMonths, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Field } from "@/components/Field";
import { BorrowerPicker } from "@/components/BorrowerPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const Choice = ({ active, onClick, title, desc, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={cn("rounded-md border p-4 text-left transition-colors", active ? "border-primary bg-primary/5 ring-1 ring-primary" : "bg-card hover:border-foreground/30")}>
    <div className="text-sm font-semibold">{title}</div>
    <div className="mt-1 text-xs text-muted-foreground">{desc}</div>
  </button>
);

function validate(f) {
  const e = {};
  if (!f.borrower_id) e.borrower_id = "Select a borrower";
  if (f.principal === "" || isNaN(+f.principal) || +f.principal <= 0) e.principal = "Principal must be greater than 0";
  if (f.apr === "" || isNaN(+f.apr) || +f.apr < 0 || +f.apr > 100) e.apr = "APR must be between 0 and 100";
  if (f.term_months === "" || !Number.isInteger(+f.term_months) || +f.term_months < 1) e.term_months = "Term must be at least 1 month";
  if (!f.start_date) e.start_date = "Enter a valid start date";
  if (!f.first_due_date) e.first_due_date = "Enter a valid first due date";
  else if (f.start_date && f.first_due_date <= f.start_date) e.first_due_date = "First due date must be after the start date";
  if (f.late_fee_value === "" || isNaN(+f.late_fee_value) || +f.late_fee_value < 0) e.late_fee_value = "Late fee must be 0 or more";
  else if (f.late_fee_type === "percent" && +f.late_fee_value > 100) e.late_fee_value = "Percent cannot exceed 100";
  if (f.grace_days === "" || !Number.isInteger(+f.grace_days) || +f.grace_days < 0) e.grace_days = "Grace period must be 0–365 days";
  return e;
}

export default function NewLoan() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const start = todayET();
  const [f, setF] = useState({
    borrower_id: params.get("borrower") || "", principal: "", apr: "", term_months: "", start_date: start,
    first_due_date: addMonths(start, 1), interest_type: "amortized", late_fee_type: "flat", late_fee_value: "25", grace_days: "10",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const appId = params.get("application");
  const [app, setApp] = useState(null);
  useEffect(() => {
    if (!appId) return;
    api.get(`/applications/${appId}`).then((r) => {
      setApp(r.data);
      setF((s) => ({ ...s, borrower_id: r.data.applicant_id, principal: (r.data.amount_cents / 100).toFixed(2), term_months: String(r.data.term_months) }));
    });
  }, [appId]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const preview = useMemo(() => {
    const p = Math.round(+f.principal * 100), a = +f.apr, n = +f.term_months;
    if (!(p > 0) || !(a >= 0) || !(n >= 1) || !Number.isInteger(n)) return null;
    const pmt = levelPayment(p, a, n);
    return { pmt, firstInterest: Math.round(p * (a / 100 / 12)), daily: (p * a) / 100 / 365 };
  }, [f.principal, f.apr, f.term_months]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/loans", { ...f, application_id: appId || undefined });
      toast.success(app ? `Application approved — loan ${data.loan_number} created` : `Loan ${data.loan_number} created`);
      navigate(`/admin/loans/${data.id}`);
    } catch (err) {
      const { message, fields } = apiError(err);
      setErrors(fields);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  const inp = (k, props = {}) => (
    <Input id={k} value={f[k]} onChange={set(k)} data-testid={`loan-${k.replace(/_/g, "-")}-input`}
      className={cn("h-11 bg-card num", errors[k] && "border-destructive")} {...props} />
  );

  return (
    <div data-testid="new-loan-page">
      <PageHeader eyebrow="Originate" title={app ? "Approve application" : "New loan"} subtitle="Terms are fixed at creation. Interest type and late-fee rule apply to this loan only." />
      {app && <div className="mb-6 rounded-md border border-[hsl(var(--gold))] bg-[hsl(var(--gold)/0.1)] px-4 py-3 text-sm" data-testid="approve-banner">
        Approving application from <b>{app.applicant_name}</b> ({app.applicant_email}) · requested {money(app.amount_cents)} over {app.term_months} months. Saving creates the loan and marks the application Approved.
      </div>}
      <form onSubmit={submit} noValidate className="grid gap-8 xl:grid-cols-[1fr_340px]" data-testid="new-loan-form">
        <div className="space-y-8">
          <section className="fade-up rounded-lg border bg-card p-6 sm:p-8">
            <h3 className="text-lg font-semibold">Borrower & terms</h3>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <Field label="Borrower" name="borrower_id" error={errors.borrower_id} className="md:col-span-2">
                <BorrowerPicker value={f.borrower_id} onChange={set("borrower_id")} invalid={!!errors.borrower_id} />
              </Field>
              <Field label="Principal (USD)" name="principal" error={errors.principal}>{inp("principal", { inputMode: "decimal", placeholder: "10000.00" })}</Field>
              <Field label="APR (%)" name="apr" error={errors.apr} hint="Nominal annual rate">{inp("apr", { inputMode: "decimal", placeholder: "12" })}</Field>
              <Field label="Term (months)" name="term_months" error={errors.term_months}>{inp("term_months", { inputMode: "numeric", placeholder: "36" })}</Field>
              <div className="hidden md:block" />
              <Field label="Start date" name="start_date" error={errors.start_date} hint="Interest accrues from this date">{inp("start_date", { type: "date" })}</Field>
              <Field label="First due date" name="first_due_date" error={errors.first_due_date} hint="Subsequent due dates fall monthly on this day">{inp("first_due_date", { type: "date" })}</Field>
            </div>
          </section>

          <section className="fade-up rounded-lg border bg-card p-6 sm:p-8" style={{ animationDelay: "80ms" }}>
            <h3 className="text-lg font-semibold">Interest method</h3>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              <Choice testid="interest-type-amortized" active={f.interest_type === "amortized"} onClick={() => set("interest_type")("amortized")}
                title="Amortized" desc="Fixed monthly payment, interest at APR/12 on the outstanding balance." />
              <Choice testid="interest-type-simple" active={f.interest_type === "simple"} onClick={() => set("interest_type")("simple")}
                title="Simple interest · Actual/365" desc="Interest accrues daily on outstanding principal at APR/365." />
            </div>
          </section>

          <section className="fade-up rounded-lg border bg-card p-6 sm:p-8" style={{ animationDelay: "140ms" }}>
            <h3 className="text-lg font-semibold">Late-fee rule</h3>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              <Choice testid="late-fee-type-flat" active={f.late_fee_type === "flat"} onClick={() => set("late_fee_type")("flat")} title="Flat amount" desc="A fixed dollar fee per late installment." />
              <Choice testid="late-fee-type-percent" active={f.late_fee_type === "percent"} onClick={() => set("late_fee_type")("percent")} title="Percent of installment" desc="A percentage of the missed installment amount." />
            </div>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              <Field label={f.late_fee_type === "flat" ? "Fee amount (USD)" : "Fee percent (%)"} name="late_fee_value" error={errors.late_fee_value}>{inp("late_fee_value", { inputMode: "decimal" })}</Field>
              <Field label="Grace period (days)" name="grace_days" error={errors.grace_days} hint="Fee is assessed once if unpaid after due date + grace">{inp("grace_days", { inputMode: "numeric" })}</Field>
            </div>
          </section>
        </div>

        <aside className="xl:sticky xl:top-8 xl:self-start">
          <div className="sidebar-surface grain relative overflow-hidden rounded-lg border p-6" data-testid="loan-preview">
            <div className="relative">
              <div className="eyebrow sidebar-muted">Estimate</div>
              <div className="mt-3 text-sm sidebar-muted">{f.interest_type === "simple" ? "Scheduled monthly due" : "Monthly payment"}</div>
              <div className="num mt-1 font-display text-4xl font-semibold" data-testid="preview-monthly-payment">{preview ? money(preview.pmt) : "—"}</div>
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between gap-3"><span className="sidebar-muted">First due</span><span>{fmtDate(f.first_due_date)}</span></div>
                {f.interest_type === "amortized" ? (
                  <div className="flex justify-between gap-3"><span className="sidebar-muted">First-month interest</span><span className="num">{preview ? money(preview.firstInterest) : "—"}</span></div>
                ) : (
                  <div className="flex justify-between gap-3"><span className="sidebar-muted">Daily interest</span><span className="num">{preview ? money(Math.round(preview.daily)) : "—"}/day</span></div>
                )}
                <div className="flex justify-between gap-3"><span className="sidebar-muted">Payments</span><span className="num">{f.term_months || "—"}</span></div>
              </div>
              <Button type="submit" disabled={busy} className="mt-8 h-11 w-full" data-testid="create-loan-submit-button">
                {busy && <Loader2 className="animate-spin" />} Create loan
              </Button>
            </div>
          </div>
        </aside>
      </form>
    </div>
  );
}
